import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import type {SetStateAction} from 'react'
import type {CommentsDisplayMode} from './utils/display-mode'

type CommentsSortConfig = {
  dateOrder: 'ascending' | 'descending'
  /** Sort by code order in supported views. Code order only shows review comments. */
  codeOrderOverride: boolean
}

function isCommentsSortConfig(value: unknown): value is CommentsSortConfig {
  if (typeof value !== 'object' || value === null) return false

  const {dateOrder, codeOrderOverride} = value as Record<string, unknown>
  return (dateOrder === 'ascending' || dateOrder === 'descending') && typeof codeOrderOverride === 'boolean'
}

const defaultCommentsSortConfig = {
  dateOrder: 'descending',
  codeOrderOverride: true,
} as const satisfies CommentsSortConfig

function validOrDefaultCommentsSortConfig(unvalidatedConfig: unknown): CommentsSortConfig {
  return isCommentsSortConfig(unvalidatedConfig) ? unvalidatedConfig : defaultCommentsSortConfig
}

export function commentsSortStorageKey(localStorageKey?: string) {
  return `comments-sort${localStorageKey ? `-${localStorageKey}` : ''}`
}

function useCommentsSortConfig(localStorageKey?: string) {
  return useLocalStorage(commentsSortStorageKey(localStorageKey), defaultCommentsSortConfig, {
    validator: isCommentsSortConfig,
  })
}

export const CommentsSort = {
  NewestFirst: 'newest-first',
  OldestFirst: 'oldest-first',
  DiffOrder: 'diff-order',
} as const

export type CommentsSort = (typeof CommentsSort)[keyof typeof CommentsSort]

/** Only the flat thread display mode can be sorted by diff/code order. */
export const supportsCodeOrderSort = (displayMode: CommentsDisplayMode) => displayMode === 'review-threads'

function resolveSortForDisplayMode(displayMode: CommentsDisplayMode, config: CommentsSortConfig): CommentsSort {
  if (config.codeOrderOverride && supportsCodeOrderSort(displayMode)) return CommentsSort.DiffOrder

  return config.dateOrder === 'ascending' ? CommentsSort.OldestFirst : CommentsSort.NewestFirst
}

/**
 * Tracks the state of comments sort direction.
 * @param localStorageKey Optional unique ID for local storage keying, so that separate instances can key separately
 * if desired. If not provided, will share the default local storage destination.
 */
export function useCommentsSortState(displayMode: CommentsDisplayMode, localStorageKey?: string) {
  const [unvalidatedSortConfig, setUnvalidatedSortConfig] = useCommentsSortConfig(localStorageKey)

  const setSort = (action: SetStateAction<CommentsSort>) => {
    setUnvalidatedSortConfig(current => {
      const validCurrentConfig = validOrDefaultCommentsSortConfig(current)
      const currentSort = resolveSortForDisplayMode(displayMode, validCurrentConfig)

      const newSort = typeof action === 'function' ? action(currentSort) : action

      // Diff order always preserves chrono order selection for unsupported views
      if (newSort === CommentsSort.DiffOrder) return {...validCurrentConfig, codeOrderOverride: true}

      return {
        ...validCurrentConfig,
        // Chrono order overrides diff order on supported views, but preserves diff order preference if set from an unsupported view
        ...(supportsCodeOrderSort(displayMode) ? {codeOrderOverride: false} : {}),
        dateOrder: newSort === CommentsSort.NewestFirst ? 'descending' : 'ascending',
      }
    })
  }

  // don't rely on internal useLocalStorage types which are just casting on parse
  const sort = resolveSortForDisplayMode(displayMode, validOrDefaultCommentsSortConfig(unvalidatedSortConfig))

  return [sort, setSort] as const
}
